/**
 * Visual "directions" for the AERO widget. Each theme is a bundle of CSS custom
 * properties applied to the widget root; components read them via var(--aero-*).
 */

import type { CSSProperties } from "react";

export type ThemeName = "cyber" | "synth" | "esports" | "stealth";

export interface Theme {
  name: ThemeName;
  label: string;
  botName: string;
  /** The header's second line, under the name — "Your Gaming Laptop
   *  Advisor". Per theme so it CAN differ; identical in all four today,
   *  because it is one desk with one job. */
  botRole: string;
  /** CSS custom properties applied to the widget root element. */
  vars: CSSProperties;
}

function theme(
  name: ThemeName,
  label: string,
  botName: string,
  botRole: string,
  v: Record<string, string>,
): Theme {
  const vars: Record<string, string> = {
    "--aero-bg": v.bg,
    "--aero-panel": v.panel,
    "--aero-panel-2": v.panel2,
    "--aero-line": v.line,
    "--aero-text": v.text,
    "--aero-muted": v.muted,
    "--aero-accent": v.accent,
    "--aero-accent-2": v.accent2,
    "--aero-accent-text": v.accentText,
    "--aero-glow": v.glow,
    "--aero-radius": v.radius,
    "--aero-clip": v.clip,
    "--aero-display-font": v.display,
    "--aero-body-font": v.body,
    "--aero-tag-font": v.tag,
    "--aero-launcher-grad": v.launcherGrad,
    "--aero-header-grad": v.headerGrad,
    "--aero-bg-image": v.bgImage,
    "--aero-bg-size": v.bgSize,
    "--aero-avatar-clip": v.avatarClip,
  };
  return { name, label, botName, botRole, vars: vars as CSSProperties };
}

export const THEMES: Record<ThemeName, Theme> = {
  cyber: theme("cyber", "Cyber Core", "Alienware Arena Assistant", "Your Gaming Laptop Advisor", {
    bg: "radial-gradient(130% 90% at 75% -10%, #0e1f44 0%, #081026 45%, #050811 100%)",
    panel: "rgba(13,26,52,.82)",
    panel2: "rgba(22,40,74,.5)",
    line: "rgba(90,200,255,.22)",
    text: "#e9f5ff",
    muted: "#8ba8cc",
    accent: "#28e7ff",
    accent2: "#a06bff",
    accentText: "#04121c",
    glow: "rgba(40,231,255,.45)",
    radius: "16px",
    clip: "inset(0 round 16px)",
    display: "'Orbitron', sans-serif",
    body: "'Chakra Petch', sans-serif",
    tag: "'Orbitron', sans-serif",
    launcherGrad: "linear-gradient(135deg,#28e7ff,#7b5cff)",
    headerGrad: "linear-gradient(135deg,rgba(40,231,255,.16),rgba(160,107,255,.12))",
    bgImage:
      "radial-gradient(circle at 1px 1px, rgba(120,210,255,.10) 1px, transparent 1.6px)",
    bgSize: "22px 22px",
    avatarClip: "circle(50%)",
  }),
  synth: theme("synth", "Synth Arcade", "Alienware Arena Assistant", "Your Gaming Laptop Advisor", {
    bg: "linear-gradient(180deg,#1c0940 0%,#2c0b56 55%,#180733 100%)",
    panel: "rgba(42,13,80,.8)",
    panel2: "rgba(60,20,104,.45)",
    line: "rgba(255,77,166,.3)",
    text: "#ffe9fb",
    muted: "#cf9fe0",
    accent: "#ff2d95",
    accent2: "#36e3ff",
    accentText: "#22001a",
    glow: "rgba(255,45,149,.5)",
    radius: "8px",
    clip: "inset(0 round 8px)",
    display: "'Audiowide', sans-serif",
    body: "'Chakra Petch', sans-serif",
    tag: "'Press Start 2P', monospace",
    launcherGrad: "linear-gradient(135deg,#ff2d95,#7a2dff)",
    headerGrad: "linear-gradient(135deg,rgba(255,45,149,.2),rgba(54,227,255,.14))",
    bgImage:
      "repeating-linear-gradient(0deg, rgba(255,77,166,.07) 0 1px, transparent 1px 30px), repeating-linear-gradient(90deg, rgba(54,227,255,.06) 0 1px, transparent 1px 30px)",
    bgSize: "auto",
    avatarClip: "inset(0 round 6px)",
  }),
  esports: theme("esports", "Esports HUD", "Alienware Arena Assistant", "Your Gaming Laptop Advisor", {
    bg: "linear-gradient(180deg,#0d0e12 0%,#08090c 100%)",
    panel: "rgba(20,22,27,.9)",
    panel2: "rgba(32,35,42,.55)",
    line: "rgba(255,76,38,.32)",
    text: "#f2f5f8",
    muted: "#8b929c",
    accent: "#ff4c26",
    accent2: "#ffcf2e",
    accentText: "#1a0600",
    glow: "rgba(255,76,38,.42)",
    radius: "3px",
    clip: "polygon(0 12px,12px 0,100% 0,100% calc(100% - 12px),calc(100% - 12px) 100%,0 100%)",
    display: "'Saira', sans-serif",
    body: "'Saira', sans-serif",
    tag: "'Saira', sans-serif",
    launcherGrad: "linear-gradient(135deg,#ff4c26,#ff1f5a)",
    headerGrad: "linear-gradient(135deg,rgba(255,76,38,.2),rgba(255,207,46,.1))",
    bgImage:
      "repeating-linear-gradient(135deg, rgba(255,76,38,.05) 0 2px, transparent 2px 11px)",
    bgSize: "auto",
    avatarClip: "polygon(50% 0,100% 25%,100% 75%,50% 100%,0 75%,0 25%)",
  }),
  stealth: theme("stealth", "Stealth", "Alienware Arena Assistant", "Your Gaming Laptop Advisor", {
    bg: "linear-gradient(180deg,#11140f 0%,#0a0c09 100%)",
    panel: "rgba(24,28,21,.82)",
    panel2: "rgba(34,40,30,.5)",
    line: "rgba(165,255,70,.18)",
    text: "#eef3ea",
    muted: "#8d958a",
    accent: "#a5ff46",
    accent2: "#54f0b0",
    accentText: "#0c1500",
    glow: "rgba(165,255,70,.3)",
    radius: "14px",
    clip: "inset(0 round 14px)",
    display: "'Rajdhani', sans-serif",
    body: "'Rajdhani', sans-serif",
    tag: "'Rajdhani', sans-serif",
    launcherGrad: "linear-gradient(135deg,#a5ff46,#43d98a)",
    headerGrad: "linear-gradient(135deg,rgba(165,255,70,.12),rgba(84,240,176,.08))",
    bgImage:
      "radial-gradient(circle at 1px 1px, rgba(165,255,70,.07) 1px, transparent 1.6px)",
    bgSize: "26px 26px",
    avatarClip: "circle(50%)",
  }),
};

export const THEME_ORDER: ThemeName[] = ["cyber", "synth", "esports", "stealth"];
